<template>
  <AppView title="會員登入" :footerShow="false">
    <main class="min-h-app bg-gray-50 pb-24">
      <section class="border-b border-gray-100 bg-white px-5 py-6">
        <p class="text-xs font-medium text-gray-400">會員中心</p>
        <h1 class="mt-2 text-xl font-bold text-gray-900">登入達人商城</h1>
        <p class="mt-2 text-sm leading-6 text-gray-500">
          使用電話號碼登入後，可查看訂單、管理遊戲 ID 與帳號綁定。
        </p>
      </section>

      <section v-if="loginMethods.length" class="mt-2 border-y border-gray-100 bg-white px-5 py-5">
        <!-- Wechat 登入 -->
        <button
          v-for="method in loginMethods"
          :key="method.name"
          type="button"
          class="mb-3 flex h-12 w-full items-center justify-center rounded-lg text-sm font-bold text-white last:mb-0"
          :class="method.class"
          @click.prevent="method.onClick"
        >
          <component :is="method.icon" class="mr-2 inline-block" />
          {{ method.text }}
        </button>
        <p class="mt-4 text-center text-sm font-bold text-primary">
          <nuxt-link :to="localePath('/auth/help')">無法登錄？查看解決方法</nuxt-link>
        </p>
      </section>

      <section class="mt-2 border-y border-gray-100 bg-white px-5 py-5">
        <!-- 會員登入 -->
        <div class="mb-5">
          <h2 class="text-sm font-bold text-gray-900">電話號碼登入</h2>
          <p class="mt-1 text-xs leading-5 text-gray-400">請輸入註冊時使用的電話號碼。</p>
        </div>
        <LoginForm @submit="submit" :disabled="posting" />
      </section>
    </main>
  </AppView>
</template>

<script>
import FacebookIcon from "@/components/icons/FacebookIcon.vue";
import WechatIcon from "@/components/icons/WechatIcon.vue";
import MPayIcon from "@/components/icons/MPayIcon.vue";
import BocIcon from "@/components/icons/BocIcon.vue";
import AlipayHKIcon from "@/components/icons/AlipayHKIcon.vue";
import LoginForm from "@/components/forms/LoginForm.vue";
import {
  getAlipayHKAuthCode,
  getAlipayHKBridgeErrorMessage,
} from "@/utils/alipayhkMiniProgramBridge";

const ALIPAYHK_PHONE_SCOPES = ["SCOPE_USER_INFO_CONTACT"];
const ALIPAYHK_AUTH_BASE_SCOPES = ["auth_base"];

export default {
  components: {
    FacebookIcon,
    WechatIcon,
    MPayIcon,
    LoginForm,
    BocIcon,
    AlipayHKIcon,
  },
  auth: "guest",
  head() {
    return {
      title: `${
        this.$store.getters["app/info"]?.name || process.env.APP_NAME
      }-會員登入`,
    };
  },
  data() {
    return {
      posting: false, // 提交中
    };
  },

  methods: {
    wechat() {
      this.login("wechat");
    },
    facebook() {
      this.login("facebook");
    },
    submit(data) {
      this.login("local", data);
    },
    mpay() {
      if (!my) return false;

      my.postMessage({
        type: "login",
      });

      my.onMessage = (e) => {
        this.login("local", {
          code: e.authCode,
          auth_type: "mpay",
        });
      };
    },
    bocmo() {
      this.login("bocmo");
    },
    async alipayhk() {
      if (typeof my === "undefined") return false;
      if (this.posting) return false;

      try {
        const result = await getAlipayHKAuthCode(ALIPAYHK_PHONE_SCOPES);
        this.loginWithAlipayHKAuthCode(result, ALIPAYHK_PHONE_SCOPES);
      } catch (e) {
        await this.alipayhkAuthBase();
      }
    },
    async alipayhkAuthBase() {
      try {
        const result = await getAlipayHKAuthCode(ALIPAYHK_AUTH_BASE_SCOPES);
        this.loginWithAlipayHKAuthCode(result, ALIPAYHK_AUTH_BASE_SCOPES);
      } catch (e) {
        this.$loader.error(
          "AlipayHK 授權失敗：" + getAlipayHKBridgeErrorMessage(e),
        );
      }
    },
    loginWithAlipayHKAuthCode(result, scopes) {
      this.login("local", {
        code: result.authCode,
        auth_type: "alipayhk",
        scopes,
        auth_success_scopes: result.authSuccessScopes || scopes,
        auth_error_scopes: result.authErrorScopes || [],
      });
    },
    login(strategie, data) {
      // 當在提交中，不再提交
      if (this.posting) return;
      this.posting = true;

      this.$auth
        .loginWith(strategie, { data: data })
        .then(() => {
          if (this.$track) {
            this.$track.event("login", {
              method: strategie,
              auth_type: data?.auth_type,
              country_code: data?.country_code,
            });
          }
        })
        .catch((err) => {
          this.posting = false;
          if (this.errors.user) {
            this.$loader.error(this.errors.user[0]);
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },

  computed: {
    title() {
      return this.mode === "register" ? "會員註冊" : "會員登入";
    },
    loginMethods() {
      const methods = [
        {
          name: "wechat",
          icon: "wechat-icon",
          text: "Wechat 登入",
          show: () => this.inWeiXin,
          onClick: () => this.wechat(),
          class: "bg-[#2dc100]",
        },
        {
          name: "facebook",
          icon: "facebook-icon",
          text: "Facebook 登入",
          show: () => false,
          onClick: () => this.facebook(),
          class: "bg-[#4267b2]",
        },
        {
          name: "mpay",
          icon: "m-pay-icon",
          text: "MPay 登入",
          show: () => this.inMpayMiniApp,
          onClick: () => this.mpay(),
          class: "bg-[#FD8204]",
        },
        {
          name: "alipayhk",
          icon: "alipay-h-k-icon",
          text: "AlipayHK 一鍵登入",
          show: () => this.isAlipayHK,
          onClick: () => this.alipayhk(),
          class: "bg-[#1677FF]",
        },
        {
          name: "boc",
          icon: "boc-icon",
          text: "中銀 登入",
          show: () => false,
          onClick: () => this.bocmo(),
          class: "from-[#D80C2A] to-[#A51917] bg-gradient-to-b",
        },
      ];
      return methods.filter((method) => method.show());
    },
  },

  mounted() {
    const { code, msg, authCode, auth_type } = this.$route.query;

    // AlipayHK OAuth 回調 — 小程式透過 URL redirect 傳回 authCode
    if (authCode && auth_type === "alipayhk") {
      this.login("local", {
        code: authCode,
        auth_type: "alipayhk",
        scopes: ALIPAYHK_AUTH_BASE_SCOPES,
        auth_success_scopes: ALIPAYHK_AUTH_BASE_SCOPES,
      });
      return;
    }

    if (msg) {
      this.$loader.error(`CODE ${code}\r\n${msg}`);
    }
  },
};
</script>

<style lang="css" scoped></style>
